import React, { useState } from "react";
import { ReactSVG } from "react-svg";
import check from "../../Asset/check.svg";
import fillCheck from "../../Asset/fill check.svg";
import { useSelector } from "react-redux";
import { selectUser } from "../../Redux/authSlice";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ButtonLoading from "../../component/ButtonLoading";
import { trackEvent } from "../../lib/mixpanel";
import { useInView } from "react-intersection-observer";

const PricingSection = () => {
  const user = useSelector(selectUser);
  const [isAnnual, setIsAnnual] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubscription = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setIsLoading(true);

    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}api/stripe/create-subscription-session`;

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: user?.EmailId,
          name: `${user?.FirstName ?? ""} ${user?.LastName ?? ""}`.trim(),
          plan: isAnnual ? "Pro_Annual" : "Pro",
        }),
      });

      let payload = null;
      try {
        payload = await res.json();
      } catch {
        // ignore
      }

      if (!res.ok) {
        const msg =
          payload?.message ||
          payload?.error ||
          res.statusText ||
          "Failed to create subscription session.";
        toast.error(msg);
        return;
      }

      if (payload?.success && payload?.clientSecret) {
        trackEvent("subscription_started", {
          plan_type: isAnnual ? "Pro_Annual" : "Pro",
          billing_cycle: isAnnual ? "annual" : "monthly",
        });
        navigate(
          `/checkout/${isAnnual ? "N9sJ0qWz7eB1" : "aJ3d8Lk29vX4"}/${payload.clientSecret}`
        );
        return;
      }

      toast.error(
        payload?.message || payload?.error || "Unexpected server response."
      );
    } catch (err) {
      toast.error(err?.message || "Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const { ref: headRef, inView: headView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const { ref: cardsRef, inView: cardsView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section className="w-full max-w-[1180px] mx-auto md:px-7 px-4 flex flex-col gap-8">
      <div
        ref={headRef}
        className={`${
          headView ? "animate-slideUp z-[3] anim-preserve" : "opacity-0 z-0"
        } flex flex-col items-center gap-3`}
      >
        <h2 className="font-jakarta font-extrabold text-[#0F1B2D] text-center text-[2.625rem] md:text-[2.4rem] lg:text-[2.8rem] xl:text-[3.125rem] leading-[1.24] tracking-[-0.02em]">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]">
            Simple Pricing.
          </span>{" "}
          Cancel Anytime.
        </h2>
        <p className="font-inter font-normal text-[#33425A] text-center text-[1.313rem] md:text-[1.15rem] lg:text-[1.2rem] xl:text-[1.438rem] leading-[1.5]">
          Start free. Upgrade when your voice becomes your keyboard.
        </p>
      </div>

      <div
        ref={cardsRef}
        className={`${
          cardsView ? "animate-slideUp z-[3] anim-preserve" : "opacity-0 z-0"
        } w-full flex justify-center items-center md:px-4 p-0`}
      >
        <div className="flex flex-col lg:flex-row lg:gap-0 items-center gap-6 max-w-5xl w-full">
          {/* Free Plan */}
          <div className="flex-1 md:rounded-s-3xl rounded-3xl bg-[#F6FBFE] lg:w-full md:w-[80%] w-full flex flex-col items-start md:gap-4 gap-3 p-6 sm:p-8 lg:p-10 lg:pr-16 lg:-mr-8">
            <h1 className="bg-gradient-to-r leading-none from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent w-max font-semibold font-inter md:text-[1.375rem] text-base">
              ZT Basic
            </h1>
            <h2 className="md:text-[3.445rem] text-[2.5rem] leading-none font-semibold font-inter">
              Free
            </h2>
            <p className="md:text-[1.375rem] text-base leading-none font-medium font-inter">
              Start your ZT journey:
            </p>
            <ul className="space-y-4">
              {[
                "15 minutes audio transcription per week",
                "Personal Dictionary",
                "Basic Prompt Selection",
                "Translate and Transcribe audio",
              ].map((feature) => (
                <li
                  key={feature}
                  className="flex items-center md:text-[1.076rem] text-sx font-inter text-[#000000] gap-2"
                >
                  <ReactSVG
                    src={check}
                    beforeInjection={(svg) => {
                      svg.setAttribute(
                        "class",
                        "md:w-[1.813rem] w-5 md:h-[1.813rem] h-5"
                      );
                    }}
                  />
                  {feature}
                </li>
              ))}
            </ul>

            <NavLink
              to={"/download"}
              className="md:mt-8 mt-6 w-full rounded-full border bg-transparent border-[#3AB1F6] hover:bg-gradient-to-r hover:from-[#3AB1F6] hover:to-[#407BFF] transition"
            >
              <span className="block w-full rounded-full bg-white py-3 text-center font-inter md:text-[1.076rem] text-sx font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] transition-all duration-300 hover:bg-none hover:text-white hover:bg-gradient-to-r hover:from-[#3AB1F6] hover:to-[#407BFF]">
                Get Started Today
              </span>
            </NavLink>
          </div>

          {/* Pro Plan */}
          <div className="flex-1 rounded-3xl bg-[#EBF7FE] border border-[#407BFF] text-black lg:w-full md:w-[80%] w-full flex flex-col items-start md:gap-4 gap-3 md:shadow-2xl shadow-xl p-6 sm:p-8 lg:p-10 relative overflow-hidden">
            <div className="flex justify-center w-full">
              <div className="bg-white p-2 rounded-full flex gap-1 w-full md:text-[1.22rem] text-sm font-semibold font-inter">
                <button
                  onClick={() => setIsAnnual(false)}
                  className={`flex-1 min-w-0 py-3 rounded-full text-sm font-medium transition ${
                    !isAnnual
                      ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                      : "text-gray-700"
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setIsAnnual(true)}
                  className={`flex-1 min-w-0 py-3 px-1 rounded-full text-sm font-medium transition flex items-center justify-center gap-1.5 whitespace-nowrap ${
                    isAnnual
                      ? "bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out text-white"
                      : "text-gray-700"
                  }`}
                >
                  Annual
                  <span className={`shrink-0 inline-flex items-center text-[0.625rem] font-bold px-1.5 py-0.5 rounded-full leading-none ${isAnnual ? "bg-white text-[#407BFF]" : "bg-[#EBF7FE] text-[#407BFF]"}`}>
                    20% off
                  </span>
                </button>
              </div>
            </div>

            <h1 className="bg-gradient-to-r leading-none from-[#3AB1F6] to-[#407BFF] bg-clip-text text-transparent w-max font-semibold font-inter md:text-[1.375rem] text-base">
              ZT Pro
            </h1>
            <h2 className="md:text-[3.445rem] text-[2.5rem] leading-none font-semibold font-inter">
              ${isAnnual ? "8.00" : "9.99"}{" "}
              <span className="md:text-[1.365rem] text-base font-inter">
                /month
              </span>
            </h2>
            <p className="md:text-[1.375rem] text-base font-medium font-inter">
              Get all premium ZT features. Everything in basic plus:
            </p>
            <ul className="space-y-4">
              {[
                "Unlimited words per week",
                "Unlimited audio transcription per week",
                "Custom prompt creation",
                "Early access to new features",
              ].map((feature) => (
                <li
                  key={feature}
                  className="flex items-center md:text-[1.076rem] text-sx font-inter text-[#000000] gap-2"
                >
                  <ReactSVG
                    src={fillCheck}
                    beforeInjection={(svg) => {
                      svg.setAttribute(
                        "class",
                        "md:w-[1.813rem] w-5 md:h-[1.813rem] h-5"
                      );
                    }}
                  />
                  {feature}
                </li>
              ))}
            </ul>

            <button
              onClick={handleSubscription}
              disabled={isLoading}
              className={`mt-8 ${
                isLoading ? "opacity-50 cursor-not-allowed" : ""
              } w-full rounded-full border bg-transparent hover:border-[#3AB1F6] border-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF] hover:from-[#3AA6E4] hover:to-[#4175EB] transition-all duration-300 ease-in-out`}
            >
              <span className="w-full flex items-center justify-center rounded-full py-3 text-center font-inter md:text-[1.068rem] text-sx font-semibold text-white">
                {isLoading ? (
                  <ButtonLoading text="Subscribing..." />
                ) : (
                  "Get Started Today"
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
